import { useNavigate } from 'react-router-dom';
import { useRef, useState, useEffect } from 'react';

import { cn } from '../../lib/cn';
import { paths } from '../../routes/paths';
import { CustomerAvatar } from './PresenceDot';
import { searchAction } from '../../store/slices/coachInsights';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectCoachSearch } from '../../store/selectors/coachInsights';
import { customerIdentity, DELETED_ACCOUNT_LABEL } from '../../components/UserIdentity';

// Slack-style global search: type to find a customer, Enter opens them.
// TODO(chat-panel): add a "Start a chat" action per row once the chat panel ships.
export const InsightsSearch = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const search = useAppSelector(selectCoachSearch);
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const root = useRef(null);

  useEffect(() => {
    const query = value.trim();
    dispatch(searchAction(query.length >= 2 ? query : ''));
    setCursor(0);
  }, [dispatch, value]);

  useEffect(() => {
    const onClick = (event) => {
      if (!root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const results = search.items;
  const showList = open && value.trim().length >= 2;
  // Rows from the previous query stay listed while the next one loads; Enter must not open them.
  const settled = !search.loading && search.query === value.trim();

  const choose = (customer) => {
    navigate(paths.dashboard.insights.customer(customer.id));
    setValue('');
    setOpen(false);
  };

  const onKeyDown = (event) => {
    if (!showList) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((c) => Math.min(c + 1, results.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (event.key === 'Enter' && settled && results[cursor]) {
      event.preventDefault();
      choose(results[cursor]);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={root} className="relative w-[328px] max-w-full">
      <input
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search customers"
        aria-label="Search customers"
        role="combobox"
        aria-expanded={showList}
        aria-controls="insights-search-results"
        aria-activedescendant={
          showList && results[cursor] ? `insights-option-${results[cursor].id}` : undefined
        }
        className="h-[30px] w-full rounded-lg border border-dq-900 bg-dq-950 px-4 font-dq text-[12px] text-dq-title outline-none placeholder:text-dq-600 focus:border-dq-700"
      />
      {showList && (
        <div
          id="insights-search-results"
          role="listbox"
          className="absolute left-0 top-[36px] z-50 w-full overflow-hidden rounded-lg border border-dq-900 bg-dq-950 shadow-lg"
        >
          {search.loading && results.length === 0 && (
            <p className="px-4 py-3 font-dq text-[12px] text-dq-disabled">Searching…</p>
          )}
          {!search.loading && results.length === 0 && (
            <p className="px-4 py-3 font-dq text-[12px] text-dq-disabled">
              {search.error || 'No customers match.'}
            </p>
          )}
          {results.map((customer, index) => {
            const identity = customerIdentity(customer);
            const heading = identity.heading ?? DELETED_ACCOUNT_LABEL;
            return (
              <div
                key={customer.id}
                onMouseEnter={() => setCursor(index)}
                className={cn(
                  'flex h-11 items-center gap-3 px-3',
                  index === cursor ? 'bg-dq-900' : 'bg-transparent'
                )}
              >
                <button
                  type="button"
                  id={`insights-option-${customer.id}`}
                  role="option"
                  aria-selected={index === cursor}
                  title={identity.title || heading}
                  onClick={() => choose(customer)}
                  className="flex min-w-0 flex-1 items-center gap-3 text-left focus:outline-none"
                >
                  <CustomerAvatar
                    initials={identity.initials}
                    active={customer.is_active_now}
                    size={28}
                  />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate font-dq text-[12px] font-medium text-dq-title">
                      {heading}
                    </span>
                    {identity.detail ? (
                      <span className="truncate font-dq text-[11px] text-dq-400">
                        {identity.detail}
                      </span>
                    ) : null}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
